"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Search, Sparkles } from "lucide-react";

/** One question as the search sees it: where it leads, what it is filed
 *  under, and the flattened question + answer it is matched on. */
export type FaqIndexRow = {
  id: string;
  href: string;
  name: string;
  section: string | null;
  text: string;
};

/** Case- and diacritic-insensitive: "cas" finds "čas", "sluzba" finds "služba". */
const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/**
 * The FAQ index's search — a progressive enhancement over the server-rendered
 * page. Nobody browses an FAQ: a visitor arrives with a question in their
 * head, so from six questions up they can type instead. Typing replaces the
 * page's static body (`[data-faq-static]`, the section cards or the list)
 * with one results list across every section, matched on the question AND
 * the answer; each hit opens its FAQ page. When nothing matches, the typed
 * question goes to the assistant as it is — in a company brain the FAQ is the
 * curated top, the assistant the long tail. Without JavaScript the page is
 * the server-rendered body, untouched.
 */
export function FaqFilter({
  rows,
  placeholder,
  noMatchTpl,
  askLabel,
  askHref,
}: {
  rows: FaqIndexRow[];
  placeholder: string;
  noMatchTpl: string;
  askLabel: string;
  /** The assistant, or null when the portal has `ai_chat` off. */
  askHref: string | null;
}) {
  const [q, setQ] = useState("");
  const folded = useMemo(() => rows.map((r) => ({ ...r, folded: fold(r.text) })), [rows]);
  const query = q.trim();
  const hits = useMemo(() => {
    const needle = fold(query);
    return needle ? folded.filter((r) => r.folded.includes(needle)) : [];
  }, [query, folded]);

  useEffect(() => {
    for (const el of document.querySelectorAll<HTMLElement>("[data-faq-static]")) {
      el.toggleAttribute("hidden", query.length > 0);
    }
  }, [query]);

  return (
    <div className="grid gap-6">
      <label className="relative block">
        <Search
          className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-foreground/35"
          aria-hidden
        />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="h-11 w-full rounded-xl border border-border/60 bg-card pr-4 pl-11 text-[15px] text-foreground transition-[border-color,box-shadow] placeholder:text-muted-foreground/70 focus:border-accent/60 focus:outline-none"
        />
      </label>

      {query && hits.length > 0 ? (
        <ul
          className="divide-y divide-border/70 overflow-hidden rounded-card bg-card"
          style={{ boxShadow: "var(--shadow-soft)" }}
          aria-live="polite"
        >
          {hits.map((r) => (
            <li key={r.id}>
              <Link
                href={r.href}
                className="group/hit flex items-start gap-4 px-5 py-3.5 no-underline transition-colors hover:bg-muted/40 md:px-6"
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-[15px] font-bold leading-snug text-foreground">
                    {r.name}
                  </span>
                  {r.section ? (
                    <span className="mt-1 block font-display text-[10.5px] font-extrabold uppercase leading-snug tracking-[0.18em] text-foreground/40">
                      {r.section}
                    </span>
                  ) : null}
                </span>
                <ArrowUpRight
                  className="mt-1 size-4 shrink-0 text-foreground/30 transition-transform group-hover/hit:-translate-y-0.5 group-hover/hit:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      {query && hits.length === 0 ? (
        <div
          className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px] text-muted-foreground"
          aria-live="polite"
        >
          <p className="!m-0">{noMatchTpl.replace("{q}", query)}</p>
          {askHref ? (
            <Link
              href={`${askHref}?q=${encodeURIComponent(query)}`}
              className="inline-flex items-center gap-2 font-semibold text-foreground no-underline transition-colors hover:text-accent"
            >
              <Sparkles className="size-4 text-accent" aria-hidden />
              {askLabel}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
